/**
 * Executive answer view: question title, provenance caption, chart.
 *
 * Executes the selected prompt deterministically against the executive's
 * current scenario selection; changing scenarios in the sidebar re-runs
 * the same question (the effect keys on prompt + scenario ids). The
 * chart renders through the analyst path's ChartVizResult — the query
 * response's chart_payload is shape-identical to a ResultCard's.
 *
 * Provenance is part of the artifact by design (design doc §8): scenario
 * names + tags, instance name, and the calculation timestamp render
 * directly under the question so screenshots carry their own attribution.
 * No stat tile in v1 (locked decision).
 *
 * Execution-record flag (design doc EXEC_WHATIF_v1_design.md §8): one
 * ledger row per answer-view *entry* (the executive picking a question),
 * never one per scenario-toggle re-run. `recordedPromptRef` holds the
 * prompt id this view last sent `record: true` for; the run effect below
 * sends `record: (recordedPromptRef.current !== prompt.id)` and updates
 * the ref right after firing. A prompt change (new selection, including
 * re-selecting from RECENT) naturally re-records because the ref still
 * holds the *previous* prompt's id; a scenario-toggle re-run (same
 * prompt.id, the scenarioKey effect re-firing) sends `record: false`.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { ChartVizResult } from 'src/components/AgenticV2/cards/results/ChartVizResult';
import { stripPromptMarkdown } from 'src/components/shared/PromptTitle';
import { HTTPError } from 'src/support/errors';

import { runExecutiveQuery } from './api';
import { clipChartPayloadToYearWindow, YearWindow } from './execYearWindow';
import { EXEC_CONTENT_CONTAINER_SX } from './layout';
import ProcessingPanel from './ProcessingPanel';
import {
  ExecutiveBrand,
  ExecutivePromptItem,
  ExecutiveQueryResponse,
} from './types';

interface AnswerViewProps {
  brand: ExecutiveBrand;
  prompt: ExecutivePromptItem;
  scenarioIds: number[];
  onBack: () => void;
}

interface AnswerState {
  status: 'loading' | 'done' | 'error';
  response?: ExecutiveQueryResponse;
  calculatedAt?: Date;
  errorMessage?: string;
}

const describeError = (e: unknown): string => {
  if (e instanceof HTTPError) {
    if (e.status === 504) {
      return 'The forecast service took too long to answer. Please try again.';
    }
    if (e.status === 403 || e.status === 404) {
      return 'This question is not available for the current selection.';
    }
    return 'The forecast service could not answer this question right now.';
  }
  return 'Something went wrong while retrieving this answer.';
};

const AnswerView: React.FC<AnswerViewProps> = ({
  brand,
  prompt,
  scenarioIds,
  onBack,
}) => {
  const [state, setState] = useState<AnswerState>({ status: 'loading' });
  // Monotonic run counter so a stale (superseded) response never wins.
  const runSeq = useRef(0);

  // Exec AI year window (C1): the instance's chart year clip, same derivation WhatIfView uses. Both bounds null
  // (unset) = show all years.
  const yearWindow = useMemo<YearWindow>(
    () => ({
      before: brand.exec_ai_years_before,
      after: brand.exec_ai_years_after,
    }),
    [brand.exec_ai_years_before, brand.exec_ai_years_after]
  );
  // Clip the query answer's payload here, before it reaches the shared analyst renderer (ChartVizResult stays
  // analyst-agnostic; the analyst chat never sees a window). Memoized so the renderer's one-time state init sees
  // a stable object — the helper returns the same reference when there is nothing to clip.
  const chartPayload = useMemo(
    () =>
      state.response
        ? clipChartPayloadToYearWindow(state.response.chart_payload, yearWindow)
        : undefined,
    [state.response, yearWindow]
  );
  // Record-flag latch (see file header): the prompt id this view last
  // sent record: true for. Starts null so the very first execution for
  // any prompt always records.
  const recordedPromptRef = useRef<number | null>(null);

  const selectedScenarios = useMemo(
    () => brand.scenarios.filter((s) => scenarioIds.includes(s.scenario_id)),
    [brand.scenarios, scenarioIds]
  );

  const scenarioKey = scenarioIds.join(',');

  useEffect(() => {
    runSeq.current += 1;
    const seq = runSeq.current;
    setState({ status: 'loading' });
    const record = recordedPromptRef.current !== prompt.id;
    recordedPromptRef.current = prompt.id;
    runExecutiveQuery(
      prompt.id,
      scenarioIds,
      selectedScenarios.map((s) => s.name),
      record
    )
      .then((response) => {
        if (runSeq.current !== seq) return;
        setState({ status: 'done', response, calculatedAt: new Date() });
      })
      .catch((e: unknown) => {
        if (runSeq.current !== seq) return;
        setState({ status: 'error', errorMessage: describeError(e) });
      });
    // scenarioKey stands in for scenarioIds; selectedScenarios derives
    // from the same inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt.id, scenarioKey]);

  const provenance = useMemo(() => {
    const scenarioPart = selectedScenarios
      .map((s) => (s.tag ? `${s.name} (${s.tag})` : s.name))
      .join(' · ');
    return `${scenarioPart} — ${brand.model_instance_name}`;
  }, [selectedScenarios, brand.model_instance_name]);

  return (
    <Box sx={{ ...EXEC_CONTENT_CONTAINER_SX, py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        Back to questions
      </Button>

      {/* Headline is already bold, so the title's **bold** markup is stripped rather than rendered. */}
      <Typography variant="h4" sx={{ mb: 1 }}>
        {stripPromptMarkdown(prompt.title)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {provenance}
        {state.status === 'done' && state.calculatedAt
          ? ` · Calculated ${state.calculatedAt.toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}`
          : ''}
      </Typography>

      {state.status === 'loading' && (
        <ProcessingPanel title="Retrieving chart data…" />
      )}

      {state.status === 'error' && (
        <Alert severity="info" sx={{ maxWidth: 560 }}>
          {state.errorMessage}
        </Alert>
      )}

      {state.status === 'done' && chartPayload && (
        <ChartVizResult chartPayload={chartPayload as never} frameChart />
      )}
    </Box>
  );
};

export default AnswerView;
